import type { SxProps, Theme } from '@mui/material';
import { Tooltip, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

/**
 * Whether the element the returned ref is on had to cut its content short. Measured rather
 * than guessed from a character count, since what fits depends on the font and the box.
 */
export function useIsTruncated<T extends HTMLElement>(...watch: unknown[]) {
  const ref = useRef<T>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new ResizeObserver(() =>
      setIsTruncated(element.scrollWidth > element.clientWidth),
    );
    observer.observe(element);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- callers pass what changes the content
  }, watch);

  return { ref, isTruncated };
}

type TruncatedTextProps = {
  children: string;
  /** Defaults to the text itself. */
  tooltip?: string;
  /** Show the tooltip even when the text fits. */
  alwaysShowTooltip?: boolean;
  sx?: SxProps<Theme>;
};

/** One-line text that offers its full value on hover, and only when it was actually cut short. */
export function TruncatedText({
  children,
  tooltip,
  alwaysShowTooltip = false,
  sx,
}: TruncatedTextProps) {
  const { ref, isTruncated } = useIsTruncated<HTMLSpanElement>(children);

  const text = (
    <Typography ref={ref} component="span" noWrap sx={{ display: 'block', minWidth: 0, ...sx }}>
      {children}
    </Typography>
  );

  return alwaysShowTooltip || isTruncated ? (
    <Tooltip title={tooltip ?? children}>{text}</Tooltip>
  ) : (
    text
  );
}
